import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('npg_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('npg_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, variant, quantity = 1) => {
    const existingIndex = items.findIndex(
      (item) => item.product_id === product.id && item.variant_id === variant.variant_id
    );

    if (existingIndex >= 0) {
      const newItems = [...items];
      newItems[existingIndex].quantity += quantity;
      setItems(newItems);
    } else {
      setItems([
        ...items,
        {
          product_id: product.id,
          product_name: product.name,
          product_slug: product.slug,
          brand: product.brand,
          image: product.images?.[0] || '',
          variant_id: variant.variant_id,
          sku: variant.sku,
          size: variant.size,
          weight_kg: variant.weight_kg,
          price: variant.price_retail_with_tax,
          quantity: quantity
        }
      ]);
    }

    toast.success('Producto agregado al carrito', {
      description: `${product.name} - ${variant.size}`,
    });
  };

  const removeItem = (variantId) => {
    setItems(items.filter((item) => item.variant_id !== variantId));
    toast.info('Producto eliminado del carrito');
  };

  const updateQuantity = (variantId, quantity) => {
    if (quantity <= 0) {
      removeItem(variantId);
      return;
    }
    setItems(items.map((item) => (item.variant_id === variantId ? { ...item, quantity } : item)));
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem('npg_cart');
  };

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalWeight = items.reduce((sum, item) => sum + (item.weight_kg || 0) * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        subtotal,
        itemCount,
        totalWeight,
        isOpen,
        setIsOpen,
        addItem,
        removeItem,
        updateQuantity,
        clearCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
